import {clamp} from '@benchling/js-utils';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {ElementMouseEvent} from 'util/Types';

import ReorderableListItem from './ReorderableListItem';

function getItemRef(index: number): string {
  return `item${index}`;
}

function getOffsetTop(node: Element | null): number {
  assert(node != null, 'Expected node to be non-null');
  return node.getBoundingClientRect().top;
}

export interface ItemData<T> {
  id: string;
  itemProps: T;
  itemClass: React.ComponentType<T>;
}

export interface ReorderableListProps<T> {
  itemDatas: Array<ItemData<T>>;
  onMoveItem: (ids: Array<string>) => void;
  onMoveItemEnd: (id: string) => void;
}

interface State {
  dragParams: {
    itemId: string;
    itemHeight: number;
    itemWidth: number;
    computeTranslateFromMouseY: (pageY: number) => number;
    itemTranslateYs: Array<number>;
  } | null;
  dragState: {translateY: number; dragIndex: number} | null;
}

/**
 * Handles drag-and-drop for a vertically reorderable list of items. Renders them inside <div>'s one after
 * another and allows them to be reordered by dragging and dropping. Works if the page is scrolled.
 *
 * Assumptions:
 * - Items are the same height
 * - Caller won't reorder the items
 * - Caller won't rerender the items with a new height while dragging
 * - User uses mouse, since it doesn't handle touch events (ex: on tablet)
 *
 * TODO(T6921): Fix this component to work with scrollable containers.
 */
class ReorderableList<T extends {} = {}> extends React.Component<ReorderableListProps<T>, State> {
  state: State = {
    // Information that is constant for the lifetime of the drag operation, like the ID of the item being
    // dragged. This is set on drag start and cleared on drag end, and nullness determines whether or not a
    // drag is happening.
    dragParams: null,
    // Information that changes as the item is being dragged, such as its position. This is updated on
    // every mousemove while dragging.
    dragState: null,
  };

  listContainerRef = React.createRef<HTMLDivElement>();

  componentWillUnmount(): void {
    this.detachDragListeners();
  }

  private getItemNode = (index: number): Element =>
    ReactDOM.findDOMNode(this.refs[getItemRef(index)]) as Element;

  private getItemTranslateY = (index: number): number =>
    getOffsetTop(this.getItemNode(index)) - getOffsetTop(this.listContainerRef.current);

  private handleDragStart = (e: ElementMouseEvent, index: number): void => {
    if (
      !assertAndContinue(
        this.state.dragParams == null,
        'Started dragging on item that already started dragging'
      )
    ) {
      return;
    }
    if (this.props.itemDatas.length <= 1) {
      return;
    }
    const itemNode = this.getItemNode(index);
    // Store how far down from its top the dragged item was clicked so it follows the mouse as the mouse
    // moves without jumps.
    const dragStartDeltaY = e.pageY - getOffsetTop(itemNode);
    const maxTranslateY = this.getItemTranslateY(this.props.itemDatas.length - 1);
    const computeTranslateFromMouseY = (
      pageY: number // Given the screen coordinates of the mouse now, figure out the new y translation of the dragged item.
    ): number =>
      // Note that we don't cache the list container's position because it changes if the page scrolls while
      // dragging. The y translation constants (min, max, etc.) are cached because they don't change.
      clamp(0, maxTranslateY, pageY - dragStartDeltaY - getOffsetTop(this.listContainerRef.current));
    this.attachDragListeners();
    this.setState({
      dragParams: {
        itemId: this.props.itemDatas[index].id,
        itemHeight: itemNode.clientHeight,
        itemWidth: itemNode.clientWidth,
        computeTranslateFromMouseY,
        // Cache y translations of all items so `handleDragMove` can determine the new index of the dragged
        // item from where its new y translation falls in the list.
        itemTranslateYs: _.range(this.props.itemDatas.length).map((itemIndex) =>
          this.getItemTranslateY(itemIndex)
        ),
      },
      dragState: {
        translateY: computeTranslateFromMouseY(e.pageY),
        dragIndex: index,
      },
    });
  };

  private newOrderedIds = (itemId: string, newDragIndex: number): Array<string> => {
    // eslint-disable-next-line no-restricted-syntax
    const oldIndex = _.findIndex(this.props.itemDatas, (item) => item.id === itemId);
    const newOrderedIds = this.props.itemDatas.map(({id}) => id);
    newOrderedIds.splice(oldIndex, 1);
    newOrderedIds.splice(newDragIndex, 0, itemId);
    return newOrderedIds;
  };

  private handleDragMove = (e: MouseEvent): void => {
    if (this.state.dragParams == null) {
      return;
    }
    const {computeTranslateFromMouseY, itemId, itemTranslateYs} = this.state.dragParams;
    const newTranslateY = computeTranslateFromMouseY(e.pageY);
    // Find the item that has y translation closest to the y translation of the dragged item.
    const newDragIndex = itemTranslateYs.findIndex((curItemTranslateY: number, index: number) => {
      if (index === itemTranslateYs.length - 1) {
        return true;
      }
      const nextItemTranslateY = itemTranslateYs[index + 1];
      return newTranslateY - curItemTranslateY < nextItemTranslateY - newTranslateY;
    });
    assert(newDragIndex !== -1, 'expected newDragIndex to be -1');
    if (!assertAndContinue(this.state.dragState != null, 'Expected non-null dragState while dragging')) {
      return;
    }
    if (newDragIndex !== this.state.dragState!.dragIndex) {
      this.props.onMoveItem(this.newOrderedIds(itemId, newDragIndex));
    }
    this.setState({
      dragState: {
        translateY: newTranslateY,
        dragIndex: newDragIndex,
      },
    });
  };

  private handleDragEnd = (e: MouseEvent): void => {
    if (!assertAndContinue(this.state.dragParams != null, 'Expected non-null dragParams while dragging')) {
      return;
    }
    if (this.props.onMoveItemEnd) {
      this.props.onMoveItemEnd(this.state.dragParams!.itemId);
    }
    this.setState({
      dragParams: null,
      dragState: null,
    });
    this.detachDragListeners();
  };

  private attachDragListeners = (): void => {
    // We need to listen to events on the whole window object since the mouse might move outside the list
    // area for the drag operation and we're still interested in the mouse y value.
    window.addEventListener('mousemove', this.handleDragMove);
    window.addEventListener('mouseup', this.handleDragEnd);
  };

  private detachDragListeners = (): void => {
    window.removeEventListener('mousemove', this.handleDragMove);
    window.removeEventListener('mouseup', this.handleDragEnd);
  };

  render(): JSX.Element {
    let draggedItemId: string | null = null;
    if (this.state.dragParams != null) {
      draggedItemId = this.state.dragParams.itemId;
    }

    const renderedItems = this.props.itemDatas.map((itemData, index) => (
      <ReorderableListItem
        key={itemData.id}
        ref={getItemRef(index)}
        itemClass={itemData.itemClass}
        itemProps={itemData.itemProps}
        onDragStart={this.handleDragStart}
        index={index}
        isInvisible={Boolean(draggedItemId) && draggedItemId === itemData.id}
      />
    ));

    if (draggedItemId != null) {
      const draggedItemData = _.findWhere(this.props.itemDatas, {id: draggedItemId});
      if (draggedItemData != null && this.state.dragState != null && this.state.dragParams != null) {
        renderedItems.unshift(
          <ReorderableListItem
            key="draggedItem"
            itemClass={draggedItemData.itemClass}
            itemProps={draggedItemData.itemProps}
            dragLayoutParams={{
              translateY: this.state.dragState.translateY,
              height: this.state.dragParams.itemHeight,
              width: this.state.dragParams.itemWidth,
            }}
          />
        );
      }
    }

    return (
      <div className="u-position-relative" ref={this.listContainerRef}>
        {renderedItems}
      </div>
    );
  }
}

export default ReorderableList;
